import {
  ProjekttervezesCsoport,
  ProjekttervezesFeladat,
  ProjekttervezesSor,
} from '../services/projekttervezesService';
import { parseApiDate } from './workDays';

export type FlatRow =
  | { kind: 'project'; row: ProjekttervezesSor; projectIndex: number }
  | {
      kind: 'group';
      csoport: ProjekttervezesCsoport;
      parentRow: ProjekttervezesSor;
      depth: number;
      siblingIndex: number;
      parentCsoportId: number | null;
    }
  | {
      kind: 'task';
      task: ProjekttervezesFeladat;
      parentRow: ProjekttervezesSor;
      depth: number;
      siblingIndex: number;
      parentCsoportId: number | null;
    };

export type SiblingItem =
  | { kind: 'group'; id: number; sortOrder: number; group: ProjekttervezesCsoport }
  | { kind: 'task'; id: number; sortOrder: number; task: ProjekttervezesFeladat };

export interface CsoportDateRange {
  start: Date | null;
  end: Date | null;
}

export function getMergedSiblingItems(
  parentCsoportId: number | null,
  groups: ProjekttervezesCsoport[],
  tasks: ProjekttervezesFeladat[],
): SiblingItem[] {
  const siblingGroups = groups
    .filter((group) => group.parentCsoportId === parentCsoportId)
    .map((group) => ({
      kind: 'group' as const,
      id: group.id,
      sortOrder: group.sortOrder,
      group,
    }));

  const siblingTasks = tasks
    .filter((task) => task.csoportId === parentCsoportId)
    .map((task) => ({
      kind: 'task' as const,
      id: task.id,
      sortOrder: task.sortOrder,
      task,
    }));

  return [...siblingGroups, ...siblingTasks].sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) {
      return left.sortOrder - right.sortOrder;
    }
    return left.id - right.id;
  });
}

export function computeCsoportDateRange(
  csoportId: number,
  groups: ProjekttervezesCsoport[],
  tasks: ProjekttervezesFeladat[],
): CsoportDateRange {
  let minStart: Date | null = null;
  let maxEnd: Date | null = null;

  const considerRange = (start: Date | null, end: Date | null) => {
    if (start && (!minStart || start < minStart)) {
      minStart = start;
    }
    if (end && (!maxEnd || end > maxEnd)) {
      maxEnd = end;
    }
  };

  tasks
    .filter((task) => task.csoportId === csoportId)
    .forEach((task) => {
      considerRange(parseApiDate(task.kezdetiDatum), parseApiDate(task.befejezesDatum));
    });

  groups
    .filter((group) => group.parentCsoportId === csoportId)
    .forEach((group) => {
      const nested = computeCsoportDateRange(group.id, groups, tasks);
      considerRange(nested.start, nested.end);
    });

  return { start: minStart, end: maxEnd };
}

function buildChildFlatRows(
  parentRow: ProjekttervezesSor,
  parentCsoportId: number | null,
  depth: number,
  groups: ProjekttervezesCsoport[],
  tasks: ProjekttervezesFeladat[],
  expandedCsoportIds: number[],
): FlatRow[] {
  const merged = getMergedSiblingItems(parentCsoportId, groups, tasks);

  return merged.flatMap((item, siblingIndex) => {
    if (item.kind === 'group') {
      const groupRow: FlatRow = {
        kind: 'group',
        csoport: item.group,
        parentRow,
        depth,
        siblingIndex,
        parentCsoportId,
      };

      if (!expandedCsoportIds.includes(item.group.id)) {
        return [groupRow];
      }

      return [
        groupRow,
        ...buildChildFlatRows(parentRow, item.group.id, depth + 1, groups, tasks, expandedCsoportIds),
      ];
    }

    return [{
      kind: 'task',
      task: item.task,
      parentRow,
      depth,
      siblingIndex,
      parentCsoportId,
    }];
  });
}

export function buildProjekttervezesFlatRows(
  rows: ProjekttervezesSor[],
  expandedTetelIds: number[],
  expandedCsoportIds: number[],
  groupsByTetelId: Record<number, ProjekttervezesCsoport[]>,
  tasksByTetelId: Record<number, ProjekttervezesFeladat[]>,
): FlatRow[] {
  return rows.flatMap((row, projectIndex) => {
    const projectEntry: FlatRow = { kind: 'project', row, projectIndex };
    if (!expandedTetelIds.includes(row.id)) {
      return [projectEntry];
    }

    const groups = groupsByTetelId[row.id] ?? [];
    const tasks = tasksByTetelId[row.id] ?? [];

    return [
      projectEntry,
      ...buildChildFlatRows(row, null, 1, groups, tasks, expandedCsoportIds),
    ];
  });
}
